if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        const photoUpload = this.form.querySelector('rtb-photo-upload');
        if (photoUpload && !photoUpload.readyForCart?.()) {
          this.handleErrorMessage('Please upload your photo, check the size and crop, then approve the preview.');
          photoUpload.querySelector('[data-photo-status]')?.focus();
          return;
        }
        const personalization = this.form.querySelector('[data-rtb-personalization]');
        if (personalization) {
          const requiredText = personalization.querySelector('[data-personalization-required]');
          requiredText.setCustomValidity(requiredText.value.trim() ? '' : requiredText.dataset.validationMessage);
          if (!requiredText.reportValidity()) {
            this.handleErrorMessage(requiredText.validationMessage);
            requiredText.focus();
            return;
          }
        }
        const engravingSelect = this.form.querySelector('[data-photo-engraving]');
        const engravingTextInput = this.form.querySelector('[data-photo-engraving-text]');
        const engravingVariant = engravingSelect?.value;
        const engravingText = engravingTextInput?.value.trim() || '';
        if (engravingVariant && (!engravingText || engravingText.split(/\r?\n/).length > 3)) {
          this.handleErrorMessage(engravingTextInput.dataset.validationMessage);
          engravingTextInput.focus();
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        const proofProperty = formData.get('properties[Designer-reviewed proof]');
        if (engravingVariant) {
          const reference = window.crypto.randomUUID();
          const quantity = parseInt(formData.get('quantity')) || 1;
          const placement = engravingSelect.selectedOptions[0].dataset.placement;
          const properties = {
            'Front engraving': placement,
            'Engraving text': engravingText,
            '_Engraving Reference': reference
          };
          const payload = new FormData();
          payload.append('items[0][id]', formData.get('id'));
          payload.append('items[0][quantity]', quantity);
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.*)\]$/);
            if (match) payload.append(`items[0][properties][${match[1]}]`, value);
          }
          for (const [key, value] of Object.entries(properties)) {
            if (key === '_Engraving Reference') payload.append(`items[0][properties][${key}]`, value);
            payload.append(`items[1][properties][${key}]`, value);
          }
          payload.append('items[1][id]', engravingVariant);
          payload.append('items[1][parent_id]', formData.get('id'));
          payload.append('items[1][quantity]', quantity);
          if (formData.has('sections')) payload.append('sections', formData.get('sections'));
          if (formData.has('sections_url')) payload.append('sections_url', formData.get('sections_url'));
          config.body = payload;
        } else if (proofProperty && proofProperty.startsWith('Requested')) {
          const properties = {};
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.*)\]$/);
            if (match) properties[match[1]] = value;
          }
          const proofReference = window.crypto.randomUUID();
          properties['_Proof Reference'] = proofReference;
          properties['Proof Approved'] = 'Pending emailed proof approval';
          const payload = {
            items: [
              { id: formData.get('id'), quantity: parseInt(formData.get('quantity')) || 1, properties },
              { id: 53005275267354, quantity: 1, properties: {
                'Artwork': properties['Original Artwork Filename'] || 'Uploaded design',
                'Approval': 'Email approval required before production',
                '_Original Artwork URL': properties['_Original Artwork URL'] || '',
                '_Proof Reference': proofReference
              } }
            ]
          };
          if (formData.has('sections')) payload.sections = formData.get('sections');
          if (formData.has('sections_url')) payload.sections_url = formData.get('sections_url');
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        } else {
          config.body = formData;
        }

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.items && response.items.length) response = { ...response, ...response.items[0] };
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}





